/**
 * Ansprechpartner EINER Firma aus dem Kontakt-Detail — die eine Abbildung, die
 * sich Vorgangskopf (v3) und Ansprechpartner-Karte (Altansicht) teilen.
 *
 * Quelle ist `GET /contact/:id?include=relations,relations-deep`: die
 * `relationsSrc` der Firma (Firma → Ziel) inklusive `contactDest.type` und der
 * Verantwortlichkeit im Feld `function`.
 *
 * Zwei Regeln, ohne die die Liste falsch wird — dieselben, die
 * `empfaenger.ts` für die Anruf-/Mail-Auswahl schon anwendet
 * (Entscheidungen 71/72):
 *
 *  1. **Ansprechpartner gibt es nur bei einer Firma.** Bei einer Person sind
 *     dieselben Kanten Haushalts- und Verwandtschaftsbeziehungen — Ehepartner
 *     und Kinder als „Ansprechpartner" aufzulisten wäre schlicht falsch.
 *  2. **Firmen-Ziele fallen raus.** Eine Firma→Firma-Kante (Tochter-,
 *     Schwester-, Mutterfirma, Beteiligung) ist die Konzernstruktur, kein
 *     Mensch. Sie erschien bisher als namenlose Zeile mit der Nummer der
 *     falschen Firma.
 */

export interface ApPerson {
    id: number;
    /** „Nachname, Vorname" — Fallback displayName, sonst „Kontakt <id>". */
    name: string;
    /** Verantwortlichkeiten aus `ContactRelation.function`, dedupliziert. */
    funcs: string[];
}

export interface AnsprechpartnerErgebnis {
    /** Nur eine Firma hat Ansprechpartner — sonst bleibt `personen` leer. */
    istFirma: boolean;
    personen: ApPerson[];
}

const FIRMA = "Firma";

const nameVon = (dest: any): string =>
    [dest?.lastname, dest?.firstName].filter(Boolean).join(", ") ||
    dest?.displayName ||
    `Kontakt ${dest?.id}`;

export function ansprechpartnerAus(detail: any): AnsprechpartnerErgebnis {
    const istFirma = String(detail?.type?.type ?? "") === FIRMA;
    if (!istFirma) return { istFirma: false, personen: [] };

    const byId = new Map<number, ApPerson>();
    for (const rel of detail?.relationsSrc ?? []) {
        const dest = rel?.contactDest;
        if (!dest?.id) continue;
        if (String(dest?.type?.type ?? "") === FIRMA) continue;

        const id = Number(dest.id);
        const eintrag: ApPerson = byId.get(id) ?? { id, name: nameVon(dest), funcs: [] };
        const func = String(rel?.function ?? "").trim();
        if (func && !eintrag.funcs.includes(func)) eintrag.funcs.push(func);
        byId.set(id, eintrag);
    }

    return {
        istFirma: true,
        personen: Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, "de")),
    };
}
